import styles from './index.module.css'

import { LuChartColumn } from '@react-icons/all-files/lu/LuChartColumn'
import { LuForward } from '@react-icons/all-files/lu/LuForward'
import { LuInfo } from '@react-icons/all-files/lu/LuInfo'
import { LuX } from '@react-icons/all-files/lu/LuX'
import React, { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import api from '@disco/api/server'
import { TRACK_SHARING } from '@disco/constants/FeatureTypes'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { formatTimestampShort } from '@disco/utils/time-utils'

import type { Track } from 'types/track'

import {
  Avatar,
  Box,
  Dialog,
  Flex,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Table,
  Tabs,
  Text,
  Tooltip,
} from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type { AggregatedStats } from './helpers'
import { getAggregates, makeSourceData, transformListenerData } from './helpers'
import SourceLink from './source-link'

type Props = {
  onClose: (...args: Array<any>) => any
  track: Track
}

const renderListener = (row: Record<string, any>) => {
  const isUnattributed = row['id'] === -1
  const username = !isUnattributed ? row['Name'] : 'Other Recipients'
  const initials = username
    .split(' ')
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')

  const avatar = (
    <Avatar
      size="2"
      radius="full"
      fallback={initials}
      className={styles.listenerAvatar}
    />
  )
  const nameEl = (
    <Flex align="center" gap="1" minWidth="0" flexGrow="1">
      <Tooltip content={username}>
        <Text size="2" truncate>
          {username}
        </Text>
      </Tooltip>
      {isUnattributed && (
        <Tooltip content="Combined activity from listeners who aren't linked to a contact.">
          {/* This tooltip is the only place the row's meaning is explained, so
              its trigger must be reachable by keyboard — Radix wires focus
              handlers into its child but doesn't make it focusable, and a
              plain span never is. */}
          <button
            type="button"
            className={styles.infoIcon}
            aria-label="About Other Recipients"
          >
            <LuInfo size={14} />
          </button>
        </Tooltip>
      )}
    </Flex>
  )

  return !isUnattributed ? (
    <RouterLink className={styles.listener} to={`/contacts/${row['id']}`}>
      {avatar}
      {nameEl}
    </RouterLink>
  ) : (
    <span className={styles.listener}>
      {avatar}
      {nameEl}
    </span>
  )
}

const Stats = ({ onClose, track }: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const isTrackSharingEnabled = useFeatureEnabled(TRACK_SHARING)
  const isPhone = useSelector(isDeviceTypePhone)

  const [listenerData, setListenerData] = useState<any[] | null>(null)
  const [sourceData, setSourceData] = useState<any[] | null>(null)
  const [aggregatedStats, setAggregatedStats] =
    useState<AggregatedStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [noData, setNoData] = useState(false)

  useEffect(() => {
    const fetchListenerData = async () => {
      setLoading(true)

      try {
        const rawListenerData = await api.getTrackStats(track.id)

        if (!rawListenerData || rawListenerData.length === 0) {
          setLoading(false)
          setNoData(true)
          return
        }

        const listeners = transformListenerData(
          rawListenerData,
          isTrackSharingEnabled,
        )
        setListenerData(listeners)
        setAggregatedStats(getAggregates(listeners))
        setSourceData(makeSourceData(rawListenerData, track))
        setLoading(false)
      } catch (error) {
        setLoading(false)
        dispatch(sendGenericError(error))
      }
    }

    fetchListenerData()
  }, [dispatch, isTrackSharingEnabled, track])

  const numCards = isTrackSharingEnabled ? 4 : 3
  const gridColumns = isPhone ? '2' : String(numCards)

  const renderListenersTable = () => (
    <Table.Root mt="3" className={styles.statsTable}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell justify="end">
            Page Views
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell justify="end">
            Downloads/Saves
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell justify="end">Streams</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Last Activity</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {(listenerData ?? []).map((row: Record<string, any>, index: number) => (
          <Table.Row key={index}>
            <Table.Cell>{renderListener(row)}</Table.Cell>
            <Table.Cell justify="end">{row['Page Views']}</Table.Cell>
            <Table.Cell justify="end">{row['Downloads/Saves']}</Table.Cell>
            <Table.Cell justify="end">{row['Streams']}</Table.Cell>
            <Table.Cell>
              {row['Last Activity']
                ? formatTimestampShort(row['Last Activity'])
                : '—'}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )

  const renderSourceTable = () => (
    <Table.Root mt="3" className={styles.statsTable}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Source</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell justify="end">
            Page Views
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell justify="end">
            Downloads/Saves
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell justify="end">Streams</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell justify="end">
            Listeners
          </Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {(sourceData ?? []).map((row: Record<string, any>, index: number) => (
          <Table.Row key={index}>
            <Table.Cell>
              <Tooltip content={row['Source'].sourceText}>
                <span className={styles.sourceCell}>
                  <SourceLink source={row['Source']} />
                </span>
              </Tooltip>
            </Table.Cell>
            <Table.Cell justify="end">{row['Page Views']}</Table.Cell>
            <Table.Cell justify="end">{row['Downloads/Saves']}</Table.Cell>
            <Table.Cell justify="end">{row['Streams']}</Table.Cell>
            <Table.Cell justify="end">
              {(row['uniqueListeners'] as any[]).length}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )

  const renderContents = () => {
    if (noData) {
      return (
        <Flex direction="column" align="center" gap="3" py="9">
          <Flex align="center" justify="center" className={styles.emptyIcon}>
            <LuChartColumn size={24} />
          </Flex>
          <Text size="3" weight="bold" mt="2">
            No stats yet
          </Text>
          <Text size="2" color="gray" align="center">
            Stats will show up here once {track.name} starts getting plays.
          </Text>
          <Link asChild>
            <RouterLink
              to={`/explorer/tracks/${track.id}/share`}
              className={styles.shareLink}
            >
              <LuForward size={16} />
              <span className={styles.shareLinkText}>Share track</span>
            </RouterLink>
          </Link>
        </Flex>
      )
    }

    if (loading) {
      return (
        <>
          <Grid columns={gridColumns} gap="3" mb="5">
            {Array.from({ length: numCards }).map((_, i) => (
              <Skeleton key={i} height="120px" />
            ))}
          </Grid>
          <Skeleton height="var(--font-size-1)" width="120px" mb="3" />
          <Flex direction="column" gap="3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height="40px" />
            ))}
          </Flex>
        </>
      )
    }

    const statCards = [
      ...(isTrackSharingEnabled
        ? [
            {
              label: 'Unique Listeners',
              value: aggregatedStats?.uniqueListeners,
            },
          ]
        : []),
      { label: 'Page Views', value: aggregatedStats?.pageViews },
      { label: 'Downloads/Saves', value: aggregatedStats?.downloads },
      { label: 'Streams', value: aggregatedStats?.streams },
    ]

    return (
      <>
        <Grid columns={gridColumns} gap="3" mb="5">
          {statCards.map(card => (
            <Box key={card.label} className={styles.statCard}>
              <Text size="2" color="gray" className={styles.statCardLabel}>
                {card.label}
              </Text>
              <Text size="7" weight="bold" className={styles.statCardValue}>
                {card.value}
              </Text>
            </Box>
          ))}
        </Grid>

        <Tabs.Root defaultValue="listeners">
          <Tabs.List>
            <Tabs.Trigger value="listeners">Listeners</Tabs.Trigger>
            <Tabs.Trigger value="source">Source</Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="listeners">
            {renderListenersTable()}
          </Tabs.Content>
          <Tabs.Content value="source">{renderSourceTable()}</Tabs.Content>
        </Tabs.Root>
      </>
    )
  }

  if (!track) return null

  return (
    <Dialog.Root open onOpenChange={open => !open && onClose()}>
      <Dialog.Content
        maxWidth="900px"
        className={styles.dialogContent}
        aria-describedby={undefined}
      >
        <div className={styles.dialogHeader}>
          <Dialog.Close className={styles.closeButton}>
            <IconButton
              type="button"
              variant="ghost"
              color="gray"
              size="2"
              aria-label="Close"
            >
              <LuX size={18} />
            </IconButton>
          </Dialog.Close>
          <Text
            size="1"
            weight="bold"
            color="gray"
            className={styles.sectionLabel}
          >
            TRACK STATS
          </Text>
          <Dialog.Title mt="1" mb="0">
            {track.name}
          </Dialog.Title>
        </div>

        <div className={styles.dialogBody}>{renderContents()}</div>

        {!loading && !noData && (
          <Text size="2" color="gray" className={styles.footer}>
            To view more activity for your recipients, and manage their details,
            visit{' '}
            <Link asChild>
              <RouterLink to="/contacts/list">Contacts</RouterLink>
            </Link>
          </Text>
        )}
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default React.memo<Props>(Stats)
